import { ITagStatus } from '@tecalisdev/infinity';
import {
	BOOLEAN_STATUS,
	IOperationPolDataTry,
	IOperationPolDetail,
	IPolGeneralData,
	IPolLifeTest,
	isAutoPolTry,
	PolStatus,
} from '../../processes/models/processes.models';
import { MenuItemAction } from '../../shared/models/master.models';
import { IOperationActivity } from '../models/operations.models';

export interface PolActivityLogContext {
	startAt?: string | null;
	endAt?: string | null;
	logsUrl?: string | null;
	blockedFacesCount?: number;
	koGeneral?: { title: string; subtitle: string } | null;
	/** process: RESULT por intento con scoring general. */
	includeGeneralResult?: boolean;
	includeAntispoof?: boolean;
	includeAntispoofLab?: boolean;
	includeFraudFaceBlockDescription?: boolean;
	facialRecognitionTitleKey?: string;
}

export interface PolActivityLogDeps {
	translate: (key: string, params?: Record<string, unknown>) => string;
	getTagStatus: (state: string) => ITagStatus;
	shouldDisplayAntifraudCheck: (
		operationState: string | undefined,
		antifraudStatus: BOOLEAN_STATUS | string | undefined,
		options?: { showOnlyKo?: boolean; alwaysShowResult?: boolean },
	) => boolean;
	isPolGeneralResultOk?: (general: IPolGeneralData) => boolean;
	isSuperadmin?: () => boolean;
}

/** Núcleo de actividad POL compartido process↔ops (sin section / never-started). */
export function appendPolActivityLogs(
	activity: IOperationActivity[],
	pol: IOperationPolDetail,
	context: PolActivityLogContext,
	deps: PolActivityLogDeps,
): void {
	const startAt = context.startAt ?? pol.start_at;
	if (startAt) {
		activity.push({
			date: startAt,
			description: [deps.translate('PROCESSES.DETAIL.INIT_OPERATION')],
			...(context.logsUrl ? { showLogs: true } : {}),
		});
	}

	if (pol.data?.length) {
		pol.data.forEach((tried: IOperationPolDataTry) => {
			if (!isAutoPolTry(tried)) {
				return;
			}
			if (tried.lifeTest?.length) {
				tried.lifeTest.forEach((lifeTest: IPolLifeTest) => {
					if (lifeTest.status !== PolStatus.OnInit) {
						activity.push({
							title: deps.translate(`MASTER.POL_TESTS.${lifeTest.lifeTestId.toUpperCase()}`),
							description: [
								lifeTest.scoring?.totalTime ? `${lifeTest.scoring.totalTime.toFixed(1)} s` : '',
								lifeTest.scoring?.result ? `${lifeTest.scoring.result.toFixed(1)} pts` : '',
							],
							icon: 'icon-user',
							status: lifeTest.status === PolStatus.OnComplete ? BOOLEAN_STATUS.OK : BOOLEAN_STATUS.KO,
						});
					}
				});
			}
			if (context.includeGeneralResult && tried.general?.scoring && deps.isPolGeneralResultOk) {
				const ok = deps.isPolGeneralResultOk(tried.general);
				activity.push({
					title: deps.translate('PROCESSES.DETAIL.RESULT'),
					description: [
						tried.general.scoring.result !== undefined && tried.general.scoring.result !== null
							? Math.round((tried.general.scoring.result + Number.EPSILON) * 10) / 10 + ' ptos'
							: '',
						ok
							? deps.translate('PROCESSES.DETAIL.POL_OK')
							: deps.translate('PROCESSES.DETAIL.POL_KO'),
					],
					icon: 'icon-user',
					status: ok ? BOOLEAN_STATUS.OK : BOOLEAN_STATUS.KO,
				});
			}
		});
	}

	const facialKey = context.facialRecognitionTitleKey ?? 'PROCESSES.DETAIL.FACIAL_RECOGNITION';
	if (deps.shouldDisplayAntifraudCheck(pol.state, pol.antifraud?.FACIAL_RECOGNITION)) {
		activity.push({
			title: deps.translate(facialKey),
			icon: 'icon-resize-viewport',
			status: pol.antifraud?.FACIAL_RECOGNITION,
		});
	}
	if (deps.shouldDisplayAntifraudCheck(pol.state, pol.antifraud?.FRAUD_FACE)) {
		activity.push({
			title: deps.translate('PROCESSES.DETAIL.FRAUD_FACE'),
			icon: 'icon-shield',
			status: pol.antifraud?.FRAUD_FACE,
		});
	}
	if (
		context.includeAntispoof &&
		deps.shouldDisplayAntifraudCheck(pol.state, pol.antifraud?.ANTISPOOF)
	) {
		activity.push({
			title: deps.translate('PROCESSES.DETAIL.ANTISPOOF'),
			icon: 'icon-shield',
			status: pol.antifraud?.ANTISPOOF,
		});
	}
	if (
		context.includeAntispoofLab &&
		deps.isSuperadmin?.() &&
		deps.shouldDisplayAntifraudCheck(pol.state, pol.antifraud?.ANTISPOOF_LAB, { alwaysShowResult: true })
	) {
		activity.push({
			title: deps.translate('PROCESSES.DETAIL.ANTISPOOF_LAB'),
			icon: 'icon-shield',
			status: pol.antifraud?.ANTISPOOF_LAB,
		});
	}

	const blockedFaces = context.blockedFacesCount ?? 0;
	if (blockedFaces > 0) {
		activity.push({
			title: deps.translate('PROCESSES.DETAIL.FRAUD_FACE_BLOCK'),
			...(context.includeFraudFaceBlockDescription
				? { description: [deps.translate('PROCESSES.DETAIL.FRAUD_FACE_BLOCK_RESULT_KO')] }
				: {}),
			icon: 'icon-shield',
			status: BOOLEAN_STATUS.KO,
		});
	}

	if (context.koGeneral) {
		activity.push({
			title: context.koGeneral.title,
			description: [context.koGeneral.subtitle],
			icon: 'icon-shield',
			status: BOOLEAN_STATUS.KO,
		});
	}

	const endAt = context.endAt ?? pol.end_at;
	if (endAt) {
		activity.push({
			description: [deps.translate('PROCESSES.DETAIL.FINISH_SCAN')],
			tag: {
				label: deps.translate(`MASTER.STATUS.${pol.state?.toUpperCase() ?? ''}`),
				status: deps.getTagStatus(pol.state ?? ''),
			},
			date: endAt,
			link: {
				label: deps.translate('GENERAL.SHOW_RESPONSE'),
				action: MenuItemAction.SHOW_JSON,
				info: { title: deps.translate('PROCESSES.DETAIL.POL'), data: pol },
			},
		});
	}
}
